<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <div class="container mt-4">
    <h3 class="mb-1">Aktiver betaling med VIVA</h3>
    <div style="font-style:italic; color:var(--bo-muted); max-width:680px" class="mb-4">
      VIVA er én betalingsløsning for både ONLINE og I BUTIKK. Onboardingen (bank, KYC, ID) fullføres
      hos VIVA — vi forhåndsutfyller det vi vet.
    </div>

    <div v-if="!loading && !locations.length" style="color:var(--bo-muted)">
      Fant ingen avdelinger for denne merchanten.
    </div>

    <div v-else style="max-width:680px">
      <div class="mb-3">
        <span class="ingredient-title">Avdeling</span>
        <select class="form-select" v-model="locationId" @change="loadStatus">
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }} ({{ loc.shortName }})</option>
        </select>
      </div>

      <!-- Status -->
      <div class="viva-card" :class="statusClass">
        <div class="viva-status-row">
          <span class="viva-badge">{{ statusLabel }}</span>
          <span v-if="message" class="viva-msg">{{ message }}</span>
        </div>

        <div v-if="!status.configured" class="mt-2 viva-amber">
          VIVA-onboarding er ikke konfigurert i systemet ennå. Kontakt CAGISTA for å aktivere.
        </div>

        <div v-else-if="status.status === 'ACTIVE'" class="mt-2 viva-ok">
          VIVA-betaling er aktivert for denne avdelingen. Gjester kan betale online og i butikk.
          <span v-if="isSuperAdmin && status.switchedBy" style="color:var(--bo-muted)">
            (Nettbetaling byttet til VIVA av {{ status.switchedBy }}.)</span>
          <span v-if="isSuperAdmin"> Kassene og kioskene byttes for seg på
            <router-link to="/terminals">Terminaler</router-link> → «Kortbetaling i kasse og kiosk».</span>
          <!-- The restaurant's own copy of the advice SUPER_ADMIN gets in the box below (2026-10-12). No fixed sum:
               what is enough depends on what each restaurant refunds, and a week of refunds they can judge. -->
          <div v-if="!isSuperAdmin" class="va-note mt-2">
            Refusjoner betales fra saldoen på VIVA-kontoen deres. La det stå igjen litt når dere overfører til banken,
            omtrent det dere refunderer i løpet av en uke, så kan dere alltid refundere med en gang — her i Backoffice
            og i kassen.
          </div>
        </div>

        <!-- Connected at VIVA, web payments not moved yet: a merchant without SaaS features is already selling,
             so the move is agreed with them and made by SUPER_ADMIN (CagistaServices switchToViva). -->
        <div v-else-if="status.status === 'READY'" class="mt-2">
          <p class="mb-2">
            VIVA-kontoen er koblet til. CAGISTA slår på VIVA-betaling for nettbestillinger på tidspunktet dere har
            avtalt med oss — til da betales nettbestillinger som før.
          </p>
          <div v-if="isSuperAdmin" class="va-switch mt-3">
            <div class="fw-semibold">Bytt nettbetaling til VIVA <span class="va-tag">kun SUPER_ADMIN</span></div>
            <div class="va-note mt-1">
              Nettbestillinger går nå via <strong>{{ providerLabel }}</strong>. Etter byttet går nye nettbestillinger
              via VIVA. Kassene påvirkes ikke — de byttes for seg på
              <router-link to="/terminals">Terminaler</router-link> → «Kortbetaling i kasse og kiosk».
              <span v-if="status.readyTime">Koblet til hos VIVA {{ formatTime(status.readyTime) }}.</span>
            </div>
            <div v-if="preview && !preview.existingSourceCode" class="va-warn mt-2">
              Avdelingen har ingen VIVA-betalingskilde, så gjesten ville ikke kommet tilbake til nettsiden etter
              betaling. Lag den først: Innstillinger → Eget domene → «Lag ny kilde».
            </div>
            <div class="d-flex align-items-center flex-wrap mt-2" style="gap:0.75rem">
              <button class="bo_btn" :disabled="switching || !canSwitch" @click="switchToViva">
                {{ switching ? 'Bytter…' : 'Bytt nettbetaling til VIVA nå' }}
              </button>
              <button class="bo_btn_secondary" :disabled="refreshing || loading" @click="refresh">
                {{ refreshing ? 'Sjekker…' : 'Sjekk status hos VIVA' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="mt-2">
          <p class="mb-2">Vi sender disse opplysningene til VIVA for å starte onboardingen:</p>
          <ul class="viva-info">
            <li><strong>Navn:</strong> {{ loc.name }}</li>
            <li><strong>Org.nr:</strong> {{ loc.vatNumber }}</li>
            <li><strong>Adresse:</strong> {{ address }}</li>
            <li><strong>E-post:</strong> {{ loc.email }}</li>
            <li><strong>Telefon:</strong> {{ loc.phone }}</li>
          </ul>
          <div class="d-flex align-items-center mt-3" style="gap:0.75rem">
            <button class="bo_btn" :disabled="starting" @click="start">
              {{ starting ? 'Starter…' : (status.status === 'PENDING' ? 'Fortsett VIVA-onboarding' : 'Start VIVA-onboarding') }}
            </button>
            <button class="bo_btn_secondary" :disabled="refreshing || loading" @click="refresh">
              {{ refreshing ? 'Sjekker…' : 'Sjekk status hos VIVA' }}
            </button>
          </div>

          <!-- SUPER_ADMIN is never sent to VIVA's invitation page: whoever accepts it there connects the account
               THEIR login is on, permanently. The merchant accepts it from their own inbox; this is the link,
               for sending them if the mail went astray. -->
          <div v-if="isSuperAdmin && invitationUrl" class="va-invite mt-3">
            <div class="fw-semibold">Invitasjonen er klar — merchanten fullfører den selv.</div>
            <div class="va-note mt-1">
              VIVA har sendt den til <strong>{{ (preview && preview.body && preview.body.email) || loc.email }}</strong>.
              Merchanten åpner e-posten, logger inn med <strong>sin egen</strong> VIVA-innlogging og godtar. Trykk så
              «Sjekk status hos VIVA». Kom e-posten ikke fram, send dem lenken:
            </div>
            <div class="d-flex align-items-center flex-wrap mt-2" style="gap:0.5rem">
              <code class="viva-account__id va-invite__url">{{ invitationUrl }}</code>
              <button class="bo_btn_secondary viva-copy" @click="copyInvitation">{{ invitationCopied ? 'Kopiert ✓' : 'Kopier lenken' }}</button>
            </div>
            <div class="va-warn mt-2">
              Ikke åpne lenken selv mens du er logget inn hos VIVA — se «Fullfør aldri invitasjonen selv» under.
            </div>
          </div>

          <div v-if="status.status === 'PENDING'" class="mt-3" style="font-size:0.9rem">
            <div class="viva-amber">
              Onboarding pågår hos VIVA. Betaling aktiveres så snart VIVA har godkjent kontoen — trykk
              «Sjekk status hos VIVA».
            </div>

            <!-- The accountId is not a debugging detail: in VIVA's demo environment there is no hosted
                 onboarding flow at all, and the account is claimed by typing this id into VIVA's
                 "Connected account id" field at signup. Without it on screen, demo onboarding is a dead end. -->
            <div v-if="status.accountId" class="viva-account mt-2">
              <span class="viva-account__label">VIVA konto-ID</span>
              <code class="viva-account__id">{{ status.accountId }}</code>
              <button class="bo_btn_secondary viva-copy" @click="copyAccountId">{{ copied ? 'Kopiert ✓' : 'Kopier' }}</button>
            </div>
            <div v-if="status.checkedTime" class="mt-1" style="color:var(--bo-ink-2)">
              VIVA-status:
              <strong>{{ status.verified ? 'Verifisert' : 'Ikke verifisert ennå' }}</strong>
              ·
              <strong>{{ status.acquiringEnabled ? 'Kan ta imot betaling' : 'Kan ikke ta imot betaling ennå' }}</strong>
              <span style="color:var(--bo-muted)"> (sjekket {{ formatTime(status.checkedTime) }})</span>
              <div v-if="status.verified && !status.acquiringEnabled" class="viva-amber">
                VIVA har verifisert kontoen, men den kan ennå ikke belaste kort. Betaling aktiveres først når
                begge er på plass.
              </div>
            </div>
            <div v-if="noRedirect" class="mt-2 viva-amber">
              VIVA ga ingen onboarding-lenke. I testmiljøet er det forventet — registrer deg hos VIVA og
              oppgi konto-ID-en over i feltet «Connected account id».
            </div>
          </div>
        </div>

        <!-- SUPER_ADMIN: what to tell the restaurant about refunds and payouts when their web payments move to VIVA. Learned on
             mollapizza's first VIVA order (2026-10-12): the refund was refused, 452 PaymentsInsufficientFundsForReversal,
             because the account held only that sale after fees. Shown before the switch, when it is said, and after. -->
        <div v-if="isSuperAdmin && (status.status === 'READY' || status.status === 'ACTIVE')" class="va-switch mt-3">
          <div class="fw-semibold">Si dette til restauranten <span class="va-tag">kun SUPER_ADMIN</span></div>
          <div class="va-note mt-1">
            <strong>Refusjoner</strong> betales fra saldoen på VIVA-kontoen deres, så de kan bare refundere når det står
            nok der. Tømmes kontoen automatisk til banken (se utbetalingsinnstillingene deres i VIVA), eller har de
            nettopp startet med VIVA, kan en refusjon feile — også en kansellering samme dag, og et salg teller først
            når det er oppgjort. Backoffice sier da at saldoen ikke rekker; den går når det står nok oppgjort salg på
            kontoen, og må gjøres før dagsoppgjøret. Anbefal dem å la det stå igjen litt, omtrent det de refunderer i løpet av en
            uke, i stedet for å tømme kontoen til banken.
          </div>
          <!-- From the founder, 2026-10-12; also in CagistaServices docs/viva-legacy-merchant-switch.md §3. -->
          <div class="va-note mt-2">
            <strong>Daglig utbetaling</strong> krever VIVAs kontoplan «Omni-channel Premium». Den velger de selv i
            VIVA-kontoen sin — tilkoblingen til CAGISTA endrer den ikke, og vi kan ikke sette den.
          </div>
        </div>

        <!--
          Shown once, here, because this is the moment the merchant goes live and the moment the
          setup checklist disappears — and it is exactly then that an order can arrive with nothing
          to announce it. Not a block: they can say they have it covered and carry on.
          AFTER the status chain above, never inside it (2026-10-12): it sat between ACTIVE and the v-else from
          2026-09-30, so the v-else hung on THIS v-if — an active location that had a till, or had dismissed this,
          was offered «Start VIVA-onboarding» again (mollapizza, right after the switch).
        -->
        <div v-if="showAlertingNotice" class="alerting-notice mt-3">
          <h6 class="alerting-notice__title">Hvordan får du vite at det kommer inn en bestilling?</h6>
          <p class="alerting-notice__body">
            Bestillingene lander under Ordrer, men ingenting varsler deg. En kasse skriver ut
            bestillingen med det samme — uten kasse er det noen som må huske å se etter ny ordre.
          </p>
          <div class="alerting-notice__actions">
            <a :href="storeUrl" target="_blank" rel="noopener" class="bo_btn" @click="dismissAlertingNotice">
              Se kassesystem og skrivere
            </a>
            <button type="button" class="bo_btn_secondary" @click="dismissAlertingNotice">
              Jeg ordner det selv
            </button>
          </div>
        </div>
      </div>

      <!-- SUPER_ADMIN only: exactly what reaches VIVA, built by the server code that sends it. The list in the
           card above is for the merchant and deliberately wider — see the note at the bottom of this box. -->
      <section v-if="isSuperAdmin && preview" class="va-admin mt-4 mb-5">
        <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
          <span class="fw-semibold">Dette sendes til VIVA</span>
          <span class="va-tag">kun SUPER_ADMIN</span>
        </div>
        <!-- The one mistake here that cannot be undone. Kept at the top of the box, above the detail. -->
        <div class="va-rule mt-2">
          <div class="fw-semibold">Fullfør aldri invitasjonen selv — merchanten gjør det med sin egen innlogging.</div>
          <ol class="va-note mb-1 mt-1">
            <li><strong>Den kan koble feil konto, for godt.</strong> Din VIVA-innlogging har flere kontoer — Cagista AS
              sin egen, CAGISTAs ISV-konto og kanskje merchantens. VIVAs invitasjonsside kobler kontoen innloggingen
              står på, og vi vet ikke hvordan den velger når én innlogging har flere. En kobling kan ikke angres
              via VIVAs API. (Det var derfor produksjonstesten 30.08.2026 ble stoppet.)</li>
            <!-- No rates in this sentence: they change in VIVA Partner Config and per merchant, and a figure written
                 here goes stale (it said 0,15 % on the web after the web fee went to 0). -->
            <li><strong>Det er merchantens beslutning.</strong> Koblingen betyr at merchanten godtar at CAGISTA trekker
              et ISV-gebyr fra utbetalingene deres — satsen står i VIVA Partner Config, eller som egen sats på
              Merchant-siden. Det samtykket skal komme fra deres innlogging og deres klikk.</li>
            <li><strong>Det er kanskje ikke tillatt.</strong> Om en bruker med «Manage account» kan godta en
              ISV-kobling, er ukjent — VIVA kan begrense det til kontoeieren.</li>
          </ol>
          <div class="va-note">
            Tilgang som «Manage account» på merchantens VIVA-konto endrer ikke dette. Den er til å aktivere kassene
            (den 9-sifrede koden i VIVAs terminal-app) og se transaksjonene deres — ikke til å godta koblingen.
          </div>
        </div>
        <div v-if="preview.error" class="va-warn mt-2">{{ preview.error }}</div>
        <template v-else>
          <div v-if="preview.existingAccountId" class="va-note mt-2">
            Avdelingen har allerede en connected account hos VIVA (<code>{{ preview.existingAccountId }}</code>).
            «{{ status.status === 'PENDING' ? 'Fortsett' : 'Start' }} VIVA-onboarding» oppretter da ingenting nytt
            og sender ingen ny e-post: den henter samme invitasjon tilbake (<code>GET</code>) og sender nettleseren dit.
          </div>
          <template v-else>
            <p class="va-note mt-2 mb-2">«Start VIVA-onboarding» gjør to kall til VIVA:</p>
            <ol class="va-note va-steps">
              <li>
                <strong>Innlogging for CAGISTA:</strong> <code>POST {{ preview.tokenUrl }}</code> med CAGISTAs
                ISV Client ID og secret fra Viva Partner Config. Ingenting om merchanten.
              </li>
              <li>
                <strong>Opprett connected account:</strong> <code>POST {{ preview.onboardingUrl || '(ikke satt)' }}</code>
                med nøyaktig denne kroppen:
                <pre class="va-json">{{ prettyBody }}</pre>
              </li>
            </ol>
            <table class="admin-table va-fields">
              <thead><tr><th>Felt</th><th>Hvem sitt</th><th>Brukes til</th></tr></thead>
              <tbody>
                <tr><td class="mono">email</td><td><strong>merchantens</strong> — avdelingens e-post i Backoffice</td>
                  <td>hit sender VIVA invitasjonen («Dear 👋 … Create Credentials»)</td></tr>
                <tr><td class="mono">returnUrl</td><td>CAGISTAs</td><td>hit sender VIVA merchanten tilbake når de er ferdige</td></tr>
                <tr><td class="mono">branding</td><td>CAGISTAs</td><td>navn, logo og farge på VIVAs invitasjonsside</td></tr>
              </tbody>
            </table>
            <div v-if="!(preview.body && preview.body.email)" class="va-warn mt-2">
              Avdelingen har ingen e-post. VIVA krever den og vil avvise kallet — legg den inn på avdelingen først.
            </div>
            <p class="va-note mt-2 mb-0">
              <strong>Sendes ikke:</strong> firmanavn, org.nr., adresse, telefon, bankkonto, eller noe om gjester og
              ordrer. VIVAs ISV-endepunkt har ingen felt for dem; VIVA henter dem selv fra merchanten. Listen i kortet
              over («Vi sender disse opplysningene til VIVA») er bevisst bredere: den får merchanten til å sjekke
              opplysningene sine før VIVAs egen kontroll, der et avvik gir avslag.
            </p>
          </template>

          <div class="va-subhead mt-3">Senere i flyten</div>
          <ul class="va-note va-steps mb-0">
            <li><strong>«Sjekk status hos VIVA»</strong> leser bare kontoen tilbake (<code>GET</code> med kontoens ID).
              Ingen merchant-data sendes.</li>
            <li v-if="preview.existingSourceCode">
              <strong>Betalingskilde:</strong> avdelingen har allerede kilde
              <code>{{ preview.existingSourceCode }}</code>, så ingen ny lages ved fullføring.
            </li>
            <li v-else-if="preview.source && preview.source.host">
              <strong>Ved fullføring</strong> lages betalingskilden — der VIVA sender gjesten etter betaling:
              <code>https://{{ preview.source.hostDisplay }}/{{ preview.source.path }}</code><span
                v-if="preview.source.hostDisplay !== preview.source.host"> (teknisk
                <code>{{ preview.source.host }}</code>)</span>, kode <code>{{ preview.source.code }}</code>, navn
              «{{ preview.source.name }}». Sendes med CAGISTAs reseller-par og merchantId-en VIVA ga oss.
            </li>
            <li v-else class="va-warn">
              <strong>Ingen betalingskilde vil bli laget</strong>: merchanten har ikke noe aktivt eget domene, og
              Storefront-domene er tomt i Viva Partner Config. Gjestene ville havnet på VIVAs egen side.
            </li>
          </ul>

          <p class="va-note mt-3 mb-0">
            <strong>E-posten er det eneste merchant-feltet som betyr noe.</strong> Den må være en innboks merchanten
            leser. Har merchanten allerede en VIVA-konto, er samme adresse som innloggingen der tryggest — VIVA har
            sagt at de da blir bedt om å logge inn, men ikke om adressen må stemme.
          </p>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import TerminalService from "@/services/terminal/TerminalService";
import TerminalVisibility from "@/util/TerminalVisibility";
import LocationService from "@/services/location/LocationService";
import VivaOnboardingService from "@/services/site/VivaOnboardingService";
import UserUtil from "@/util/UserUtil";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";

export default {
  name: "VivaOnboarding",
  components: { LoadingSpinner, ConfirmDialogue },
  data() {
    return {
      loading: true,
      locations: [],
      locationId: '',
      status: { configured: false, status: 'NOT_STARTED', onlinePaymentProvider: '' },
      terminalCount: -1,           // -1 until answered; the notice never shows on a guess
      alertingNoticeSeen: false,
      starting: false,
      refreshing: false,
      copied: false,
      noRedirect: false,
      message: '',
      preview: null,               // SUPER_ADMIN: what Start would send to VIVA
      invitationUrl: '',           // SUPER_ADMIN: shown instead of being sent there
      invitationCopied: false,
      switching: false
    };
  },
  computed: {
    isSuperAdmin() {
      return UserUtil.isSuperAdmin();
    },
    /** What web orders pay with today, in words. */
    providerLabel() {
      return { DINTERO: 'Dintero', SWEDBANK_PAY: 'Swedbank Pay', VIVA: 'VIVA' }[this.status.onlinePaymentProvider]
        || 'ingen nettbetaling';
    },
    /** The server refuses without a payment source too; this only keeps the button honest. */
    canSwitch() {
      return !!(this.preview && this.preview.existingSourceCode);
    },
    prettyBody() {
      return this.preview && this.preview.body ? JSON.stringify(this.preview.body, null, 2) : '';
    },
    loc() {
      return this.locations.find((l) => l.id === this.locationId) || {};
    },
    address() {
      const l = this.loc;
      const line2 = [l.zipCode, l.city].filter(Boolean).join(' ');
      return [l.street, line2].filter(Boolean).join(', ');
    },
    statusLabel() {
      if (!this.status.configured) return 'Ikke konfigurert';
      if (this.status.status === 'ACTIVE') return 'Aktivert ✓';
      if (this.status.status === 'READY') return 'Koblet til — venter på bytte';
      if (this.status.status === 'PENDING') return 'Under behandling';
      return 'Ikke startet';
    },
    statusClass() {
      if (this.status.status === 'ACTIVE') return 'viva-card--active';
      if (this.status.status === 'PENDING' || this.status.status === 'READY') return 'viva-card--pending';
      return '';
    },
    /**
     * Live, no till, and they have not answered yet.
     *
     * `terminalCount` starts at -1 and is only set once the answer is in, so an unreadable reply
     * leaves this hidden. Telling a merchant who owns a till that nothing will tell them is worse
     * than saying nothing.
     */
    showAlertingNotice() {
      return this.status.status === 'ACTIVE'
          && this.terminalCount === 0
          && !this.alertingNoticeSeen;
    },
    /* Absolute: the shop is a different app. Host confirmed answering 200 on 2026-09-30. */
    storeUrl() {
      return import.meta.env.PROD ? 'https://cagista.com/store' : 'http://localhost:3030/store';
    },
    alertingNoticeKey() {
      return 'viva_alerting_notice_' + this.locationId;
    }
  },
  mounted() {
    LocationService.listLocations().then(
      (res) => {
        this.locations = (res.data || []).filter((l) => l && l.id);
        if (this.locations.length) {
          this.locationId = this.locations[0].id;
          this.loadStatus();
        } else {
          this.loading = false;
        }
      },
      (err) => { console.log(err); this.loading = false; }
    );
  },
  methods: {
    loadStatus() {
      if (!this.locationId) { this.loading = false; return; }
      this.loading = true;
      this.message = '';
      this.copied = false;
      this.noRedirect = false;
      this.invitationUrl = '';
      this.loadTerminalCount();
      this.loadPreview();
      VivaOnboardingService.getStatus(this.locationId).then(
        (res) => { this.status = res.data || this.status; this.loading = false; },
        (err) => { console.log(err); this.loading = false; }
      );
    },

    loadPreview() {
      this.preview = null;
      if (!this.isSuperAdmin || !this.locationId) return;
      VivaOnboardingService.getPreview(this.locationId).then(
        (res) => { this.preview = res.data || null; },
        (err) => { console.log(err); }
      );
    },

    loadTerminalCount() {
      this.terminalCount = -1;
      // Per location: a merchant with two venues can have a till in one of them.
      this.alertingNoticeSeen = localStorage.getItem(this.alertingNoticeKey) === '1';
      TerminalService.getTerminalsForLocation(this.locationId).then(
        (res) => { this.terminalCount = TerminalVisibility.forMerchantView(res.data).length; },
        (err) => { console.log(err); }
      );
    },

    /**
     * Answered once per location. localStorage, not a setting on the merchant: this is a nudge at a
     * moment, not a preference worth a document and a migration — and seeing it a second time on
     * another computer is a mild repeat rather than a failure.
     */
    dismissAlertingNotice() {
      this.alertingNoticeSeen = true;
      try {
        localStorage.setItem(this.alertingNoticeKey, '1');
      } catch (e) {
        // A private window refuses. The notice simply returns next time, which is harmless.
      }
    },
    /**
     * Asks VIVA, rather than re-reading our own doc. This is what finishes onboarding when no webhook
     * arrives - which is every time in demo, and always in dev.
     */
    refresh() {
      if (this.refreshing || !this.locationId) return;
      this.refreshing = true;
      this.message = '';
      VivaOnboardingService.refresh(this.locationId).then(
        (res) => {
          const data = res.data || {};
          this.status = data;
          if (data.error) this.message = data.error;
          this.refreshing = false;
          this.loadPreview();      // completion creates the payment source the switch depends on
        },
        (err) => {
          console.log(err);
          this.message = 'Kunne ikke hente status fra VIVA. Prøv igjen.';
          this.refreshing = false;
        }
      );
    },
    async switchToViva() {
      if (this.switching) return;
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Bytt nettbetaling til VIVA',
        message: 'Fra nå går nye nettbestillinger for ' + (this.loc.name || 'avdelingen') + ' via VIVA i stedet for '
          + this.providerLabel + '. Kassene påvirkes ikke. Er tidspunktet avtalt med merchanten?',
        okButton: 'Bytt nå',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      this.switching = true;
      this.message = '';
      VivaOnboardingService.switchToViva(this.locationId).then(
        (res) => {
          this.status = res.data || this.status;
          this.switching = false;
        },
        (err) => {
          this.message = (err.response && err.response.data && err.response.data.error)
            || 'Kunne ikke bytte til VIVA. Prøv igjen.';
          this.switching = false;
        }
      );
    },
    copyInvitation() {
      const url = this.invitationUrl;
      if (!url) return;
      const done = () => { this.invitationCopied = true; setTimeout(() => { this.invitationCopied = false; }, 2000); };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done, () => this.legacyCopy(url, done));
      } else {
        this.legacyCopy(url, done);
      }
    },
    copyAccountId() {
      const id = this.status.accountId;
      if (!id) return;
      // Clipboard API needs a secure context; the fallback keeps this working on plain http in dev.
      const done = () => { this.copied = true; setTimeout(() => { this.copied = false; }, 2000); };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(id).then(done, () => this.legacyCopy(id, done));
      } else {
        this.legacyCopy(id, done);
      }
    },
    legacyCopy(text, done) {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); done(); } catch (e) { console.log(e); }
      document.body.removeChild(el);
    },
    // Built by hand rather than toLocaleString: nb-NO puts a comma between the date and the time, and
    // whether the year appears at all shifts with the ICU version. 27.08.2026 14:50, always.
    formatTime(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, '0');
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear()
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    },
    start() {
      if (this.starting) return;
      this.starting = true;
      this.message = '';
      VivaOnboardingService.start(this.locationId).then(
        (res) => {
          const data = res.data || {};
          if (data.url && this.isSuperAdmin) {
            // NOT followed. Whoever accepts VIVA's invitation connects the account their own login is on —
            // for a SUPER_ADMIN that can be Cagista AS's, permanently. The merchant accepts it from their inbox.
            this.invitationUrl = data.url;
          } else if (data.url) {
            window.location.href = data.url; // the merchant themselves: on to VIVA's hosted onboarding
            return;
          }
          if (data.configured === false) {
            this.message = 'VIVA-onboarding er ikke konfigurert ennå.';
          } else if (data.error) {
            this.message = data.error;
          } else if (data.accountId && !data.url) {
            // Account created but VIVA sent no onboarding link - expected in demo. Surface the id
            // instead of leaving the merchant on a page that looks like nothing happened. Not when there
            // IS a link that SUPER_ADMIN was shown rather than sent to — that said «no link» under the link.
            this.noRedirect = true;
          }
          if (data.status) this.status.status = data.status;
          if (data.accountId) this.status.accountId = data.accountId;
          this.starting = false;
        },
        (err) => {
          console.log(err);
          this.message = 'Kunne ikke starte onboarding. Prøv igjen.';
          this.starting = false;
        }
      );
    }
  }
};
</script>

<style scoped>
/* A question, not a warning. The merchant has just succeeded at something; this tells them what
   happens next, and the amber edge is the same one the Ordrer notice uses so the two read as one
   message seen twice rather than two problems. */
.alerting-notice {
  padding: 14px 16px;
  border: 1px solid var(--bo-line);
  border-left: 3px solid #ECB109;
  border-radius: 8px;
  background: var(--bo-surface-2);
}

.alerting-notice__title { margin: 0 0 6px; font-weight: 700; }
.alerting-notice__body { margin: 0 0 12px; color: var(--bo-ink-2); }
.alerting-notice__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

.viva-card { border: 1px solid var(--bo-line); border-radius: 10px; padding: 16px 18px; background: var(--bo-surface); }
.viva-card--active { background: var(--bo-surface-2); border-color: #b7e2c0; }
.viva-card--pending { background: var(--bo-brand-tint); border-color: #ffd591; }
.viva-status-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.viva-badge { font-weight: 600; }
.viva-msg { color: var(--bo-alert-ink); }
/* Type on the amber tint and the dark ground alike: the -ink tokens move with the theme (Backoffice CLAUDE.md). */
.viva-amber { color: var(--bo-brand-ink); }
.viva-ok { color: var(--bo-success-ink); }
.viva-info { list-style: none; padding: 0; margin: 0; }
.viva-info li { float: none; margin-bottom: 3px; }
.viva-account { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.viva-account__label { color: var(--bo-ink-2); }
.viva-account__id { background: var(--bo-surface-2); border: 1px solid var(--bo-line); border-radius: 4px; padding: 2px 6px; font-size: 0.85rem; }
.viva-copy { min-width: 0; padding: 2px 10px; }

/* The SUPER_ADMIN box: dashed, so it reads as ours and not the merchant's. */
.va-admin { border: 1px dashed var(--bo-line); border-radius: 10px; padding: 16px 18px; background: var(--bo-surface); }
.va-tag { font-size: 0.72rem; color: var(--bo-muted); background: var(--bo-surface-2); border-radius: 20px; padding: 2px 10px; }
.va-note { font-size: 0.85rem; color: var(--bo-ink-2); line-height: 1.5; }
.va-subhead { font-weight: 600; font-size: 0.9rem; }
.va-steps { padding-left: 1.2rem; }
.va-steps li { float: none; margin-bottom: 6px; }
.va-json {
  margin: 6px 0 0; padding: 8px 10px; font-size: 0.8rem; line-height: 1.4;
  background: var(--bo-surface-2); border: 1px solid var(--bo-line); border-radius: 6px;
  white-space: pre-wrap; word-break: break-all; color: var(--bo-ink-2);
}
.va-fields { width: 100%; font-size: 0.82rem; margin-top: 10px; }
.va-fields td, .va-fields th { padding: 0.3rem 0.5rem; vertical-align: top; }
.va-fields th { color: var(--bo-muted); font-weight: 600; }
.va-warn { font-size: 0.85rem; color: var(--bo-alert-ink); }
/* The irreversible-mistake rule: red edge, normal text colour so it reads in both themes. */
.va-rule { border: 1px solid #f3c9c9; border-left: 3px solid #b00020; background: var(--bo-bad-bg); border-radius: 8px; padding: 10px 12px; }
.va-rule ol { padding-left: 1.2rem; }
.va-rule li { float: none; margin-bottom: 4px; }
.va-invite { border: 1px solid var(--bo-line); border-left: 3px solid #2E7D32; border-radius: 8px; padding: 10px 12px; background: var(--bo-surface-2); }
.va-invite__url { word-break: break-all; }
.va-switch { border: 1px dashed var(--bo-line); border-radius: 8px; padding: 10px 12px; background: var(--bo-surface); }
</style>
